import type { AbstractControl, FormGroup } from '@angular/forms';
import type { Form, FormField } from '../../interfaces/form.interface';
import { InputType } from '../../interfaces/input.interface';
import { FormOperations } from '../form-operations';

export interface ReactiveFormSubmitValidationPresentation {
  readonly title: string;
  readonly summary: string;
}

type ReactiveFormSubmitValidationErrorFormatter = (
  errors: Record<string, string>,
) => string;

/** Builds consistent validation summaries for visible, submittable form controls. */
export class ReactiveFormSubmitValidationPresenter<T> {
  private formOperations: FormOperations<T> | null = null;

  public rebuild(form: Form<T> | null, model: FormGroup | null): void {
    this.formOperations =
      form && model ? new FormOperations(form, model) : null;
  }

  public refreshControlValidity(model: FormGroup): void {
    const controls = model.controls as Record<string, AbstractControl>;

    Object.values(controls).forEach((control) =>
      control.updateValueAndValidity({ emitEvent: false }),
    );
    model.updateValueAndValidity({ emitEvent: false });
  }

  public hasInvalidSubmittableControls(
    form: Form<T>,
    model: FormGroup,
  ): boolean {
    const formOperations = this.getFormOperations(form, model);
    const controls = model.controls as Record<string, AbstractControl>;

    return Object.entries(controls).some(
      ([fieldName, control]) =>
        control.invalid && this.isSubmittableControl(formOperations, fieldName),
    );
  }

  public present(
    form: Form<T>,
    model: FormGroup,
  ): ReactiveFormSubmitValidationPresentation {
    const formOperations = this.getFormOperations(form, model);
    const controls = model.controls as Record<string, AbstractControl>;
    const invalidFields = Object.entries(controls).reduce<string[]>(
      (acc, [field, control]) => {
        if (
          !control?.invalid ||
          !this.isSubmittableControl(formOperations, field)
        ) {
          return acc;
        }

        const fieldInfo = formOperations.getFieldByControlName(field) as
          | FormField<T>
          | null
          | undefined;
        const errors = control?.errors ?? {};
        const messages = Object.keys(errors)
          .filter((key) => key in this.errorMessages)
          .map((key) =>
            this.errorMessages[key]?.(errors as Record<string, string>),
          )
          .join(' ');
        const fieldLabel = String(fieldInfo?.label ?? field).trim() || field;

        acc.push(`${fieldLabel} ${messages || ': Error desconocido.'}`);
        return acc;
      },
      [],
    );

    model.markAllAsTouched();

    return {
      title: 'Revisa los campos indicados.',
      summary: `No hemos podido guardar todavía. Revisa estos campos:<br>- ${invalidFields.join('<br>- ')}`,
    };
  }

  private get errorMessages(): Record<
    string,
    ReactiveFormSubmitValidationErrorFormatter
  > {
    return {
      required: () => 'es obligatorio.',
      maxlength: () => 'ha excedido la longitud máxima.',
      email: () => 'no es un correo válido.',
      invalidPattern: (errors) => errors['invalidPattern'] ?? '',
      passwordRequired: (errors) => errors['passwordRequired'] ?? '',
      passwordRepeatRequired: (errors) =>
        errors['passwordRepeatRequired'] ?? '',
      passwordMismatch: (errors) => errors['passwordMismatch'] ?? '',
      repeatableRowInvalid: (errors) => errors['repeatableRowInvalid'] ?? '',
      invalidTime: (errors) => errors['invalidTime'] ?? '',
      invalidSpanishId: (errors) => errors['invalidSpanishId'] ?? '',
      spanishIdTaken: (errors) => errors['spanishIdTaken'] ?? '',
      duplicateValue: (errors) => errors['duplicateValue'] ?? '',
    };
  }

  private isSubmittableControl(
    formOperations: FormOperations<T>,
    controlName: string,
  ): boolean {
    const field = formOperations.getFieldByControlName(controlName) as
      | FormField<T>
      | null
      | undefined;

    if (!field) {
      return false;
    }

    return (
      field.conditions.isVisible &&
      field.input.type !== InputType.EMPTY &&
      field.input.type !== InputType.EXTERNAL
    );
  }

  private getFormOperations(
    form: Form<T>,
    model: FormGroup,
  ): FormOperations<T> {
    if (!this.formOperations) {
      this.formOperations = new FormOperations(form, model);
    }

    return this.formOperations;
  }
}
